// @dynamic-i18n-keys
//   addToGroup.notification.added
//   addToGroup.notification.suggested
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { BiData, AddToGroupClickBiEvent } from '@wix/da-bi/pkg/events';
import AddToFaveIcon from '@wix/da-ds/pkg/Icons/24x24/AddedToFav';
import ErrorIcon from '@wix/da-ds/pkg/Icons/CloseBold';
import Notification from '../../../Notifications/Notification';
import SystemNotification from '../../../SystemNotification';
import { GroupAddedNotificationParams } from '../../redux/types';
import { usePopover } from '../../../Popovers/redux/hooks';
import { PopoverType } from '../../../Popovers/redux/types';
import { useCustomNotifications } from '../../../Notifications/CustomNotificationContext';
import { NotificationType } from '../../../Notifications';

import s from './GroupAddedNotification.scss';

export interface Props extends GroupAddedNotificationParams {
  id: number;
  queueName: string;
}

export const GroupAddedNotification: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  queueName,
  success,
  error,
  deviation,
  needsVote,
  actionbarType,
  group: { username },
}) => {
  const { t } = useTranslation();
  const group = username || t('common.group');
  const messageType = needsVote ? 'suggested' : 'added';
  const errorMessage = error || t(`addToGroup.notification.fail`);

  const { pushPopover: pushAddToGroupPopover } = usePopover(
    actionbarType === 'primary'
      ? PopoverType.ADD_TO_GROUP_PRIMARY
      : PopoverType.ADD_TO_GROUP_SECONDARY
  );

  const { removeNotification } = useCustomNotifications([NotificationType.AddToGroup]);

  const onAddToAnotherGroup = () => {
    pushAddToGroupPopover();
    removeNotification(id);
  };

  return (
    <Notification
      id={id}
      queueName={queueName}
      cardComponent={SystemNotification}
      cardProps={{
        iconComponent: success ? AddToFaveIcon : ErrorIcon,
        backgroundDeviation: deviation,
        ...(error && {
          multiLine: true,
          limitTextWidth: false,
        }),
        actions: [
          {
            label: t('addToGroup.notification.addToAnotherGroup'),
            onClick: onAddToAnotherGroup,
            biData: BiData<AddToGroupClickBiEvent>({
              evid: 340,
              deviationid: deviation.deviationId,
              sectionname: success ? 'add_to_another_group' : 'add_to_group_error',
            }),
          },
        ],
      }}
    >
      <div className={s['text']}>
        {success ? (
          t(`addToGroup.notification.${messageType}`, {
            postProcess: ['reactPostprocessor', 'featureBrancher'],
            group: <a href={username && Url.userLink(username)}>{group}</a>,
          })
        ) : (
          <span>{errorMessage}</span>
        )}
      </div>
    </Notification>
  );
};
GroupAddedNotification.displayName = 'GroupAddedNotification';

export default GroupAddedNotification;
